<script setup>
/**
 * Step two of campaign creation: which template, for the channel already
 * chosen. A category rail on the left, a grid on the right — the dashed "start
 * from scratch" card first, then the seeded templates for this channel.
 *
 * Selection emits upward; the dialog owns what a pick means, which differs per
 * channel (live chat creates a draft and opens the builder, the text channels
 * open their campaign dialog carrying the template).
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import TemplateCard from './TemplateCard.vue';
import TextTemplateCard from './TextTemplateCard.vue';
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_ALL,
  TEMPLATE_CHANNELS,
  filterTemplatesByCategory,
  filterTemplatesByChannel,
} from 'dashboard/components-next/Campaigns/helpers/campaignGallery';

const props = defineProps({
  channel: {
    type: String,
    required: true,
  },
  templates: {
    type: Array,
    required: true,
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'scratch']);

const { t } = useI18n();

const activeCategory = ref(TEMPLATE_CATEGORY_ALL);

const channelTemplates = computed(() =>
  filterTemplatesByChannel(props.templates, props.channel)
);

// Only the categories this channel actually seeds: the text libraries are
// smaller, so an empty rail entry would just be a dead end.
const categories = computed(() => {
  const seeded = new Set(
    channelTemplates.value.map(({ category }) => category)
  );
  return [
    TEMPLATE_CATEGORY_ALL,
    ...TEMPLATE_CATEGORIES.filter(category => seeded.has(category)),
  ].map(value => ({
    value,
    label: t(`CAMPAIGN.GALLERY.CATEGORIES.${value.toUpperCase()}`),
  }));
});

const visibleTemplates = computed(() =>
  filterTemplatesByCategory(channelTemplates.value, activeCategory.value)
);

const cardComponent = computed(() =>
  props.channel === TEMPLATE_CHANNELS.LIVE_CHAT
    ? TemplateCard
    : TextTemplateCard
);

const showScratchCard = computed(
  () => activeCategory.value === TEMPLATE_CATEGORY_ALL
);

const SCRATCH_DESCRIPTION_KEYS = {
  [TEMPLATE_CHANNELS.LIVE_CHAT]: 'CAMPAIGN.GALLERY.SCRATCH.DESCRIPTION',
  [TEMPLATE_CHANNELS.SMS]: 'CAMPAIGN.GALLERY.SCRATCH.SMS_DESCRIPTION',
  [TEMPLATE_CHANNELS.WHATSAPP]: 'CAMPAIGN.GALLERY.SCRATCH.WHATSAPP_DESCRIPTION',
};

const scratchDescription = computed(() =>
  t(SCRATCH_DESCRIPTION_KEYS[props.channel])
);

const showEmptyCategory = computed(
  () =>
    !props.isFetching &&
    !showScratchCard.value &&
    !visibleTemplates.value.length
);

// The channels seed different categories, so a category that does not exist on
// the channel being switched to would show an empty grid.
watch(
  () => props.channel,
  () => {
    activeCategory.value = TEMPLATE_CATEGORY_ALL;
  }
);

const categoryClasses = isActive => [
  'flex items-center w-full h-8 px-3 rounded-lg font-medium text-label-small transition-colors text-left',
  isActive
    ? 'bg-n-solid-active text-n-slate-12 shadow-sm outline outline-1 outline-n-container'
    : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12',
];
</script>

<template>
  <div class="flex flex-1 min-h-0">
    <nav
      :aria-label="t('CAMPAIGN.GALLERY.CATEGORY_GROUP_LABEL')"
      class="flex flex-col flex-shrink-0 w-44 gap-1 px-4 py-2 overflow-y-auto"
    >
      <button
        v-for="category in categories"
        :key="category.value"
        type="button"
        :aria-pressed="activeCategory === category.value"
        :class="categoryClasses(activeCategory === category.value)"
        @click="activeCategory = category.value"
      >
        {{ category.label }}
      </button>
    </nav>

    <div class="flex-1 min-h-0 px-6 py-2 overflow-y-auto">
      <div class="grid grid-cols-1 gap-3 pb-4 sm:grid-cols-2">
        <button
          v-if="showScratchCard"
          type="button"
          :disabled="isCreating"
          class="flex flex-col items-center justify-center gap-2 p-6 text-center transition-colors border-2 border-dashed rounded-xl border-n-weak text-n-slate-11 hover:border-n-slate-8 hover:text-n-slate-12 focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-60 disabled:pointer-events-none min-h-44"
          @click="emit('scratch')"
        >
          <Icon icon="i-lucide-plus" class="size-6" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.GALLERY.SCRATCH.TITLE') }}
          </span>
          <span class="text-xs">
            {{ scratchDescription }}
          </span>
        </button>

        <template v-if="isFetching">
          <Skeleton v-for="card in 4" :key="card" class="h-44" />
        </template>

        <component
          :is="cardComponent"
          v-for="template in visibleTemplates"
          :key="template.id"
          :template="template"
          :disabled="isCreating"
          @select="emit('select', template)"
        />
      </div>

      <p
        v-if="showEmptyCategory"
        class="py-10 mb-0 text-sm text-center text-n-slate-11"
      >
        {{ t('CAMPAIGN.GALLERY.EMPTY_CATEGORY') }}
      </p>
    </div>
  </div>
</template>
